import React from 'react'
import { useQuery } from 'react-query';

import { buildFieldsValidationErrors, padZero } from '../../utils/utils'
import { removeAccents } from '../../utils/string-utils'

import { DOCUMENT_TYPES, getDriverInfo } from '../../services/GetDriverInfo';

export function checkDriverInfoStepEditMode(searchUrl) {
  return searchUrl.includes('?edit=true')
}

/**
 * Moves the focus to the next field of a form, so the user does not have to
 * realize by himself that there are more fields to fill below.
 *
 * `order` must be a stable array (declare it at module level) with the field
 * names in the same order they are rendered.
 *
 * Fields that are not registered (not mounted, or not focusable at all, like
 * the options input) and disabled fields are skipped.
 *
 * The focus must be moved synchronously inside the user event handler: on iOS
 * Safari a programmatic focus only raises the soft keyboard when it happens
 * within the user gesture task, so it must not be wrapped in a timer.
 */
export function useFocusChain(order) {
  const refs = React.useRef({})

  const registerRef = React.useCallback(name => element => {
    if (element) {
      refs.current[name] = element
    } else {
      delete refs.current[name]
    }
  }, [])

  const focusNext = React.useCallback(name => {
    const currentIndex = order.indexOf(name)
    if (currentIndex < 0) return

    for (let i = currentIndex + 1; i < order.length; i++) {
      const element = refs.current[order[i]]
      if (!element) continue
      // react-select exposes the component instance, plain inputs the DOM node
      const isDisabled = element.props ? element.props.isDisabled : element.disabled
      if (isDisabled) continue
      element.focus()
      return
    }
  }, [order])

  return { registerRef, focusNext }
}

export const validValidationObject = { isValid: true };

export function DriverFieldsErrors({ fieldsValidation }) {
  const fieldsValidationFiltered = buildFieldsValidationErrors(fieldsValidation)

  return fieldsValidationFiltered.length > 0 ? (
    <div className='error-message'>
      {fieldsValidationFiltered.map(v =>
        <p key={v.message}>{v.message}</p>
      )}
    </div>
  ) : null
}

export function removePtDriversLicenceInvalidSpaces(plateNumber) {
  if(!plateNumber) return plateNumber
  const index = plateNumber.search(/[0-9]/g)
  const init = plateNumber.slice(0, index)
  const end = plateNumber.slice(index)
  return init.replaceAll(' ', '') + end
}

export const MIN_DOCUMENT_ID_DIGITS = 1


export function filterCountries(list, value) {
  if (!value) {
    return list;
  }
  return list
    .filter(c =>
      removeAccents(c.name)
        .toLowerCase()
        .startsWith(removeAccents(value).toLowerCase())
    )
    .slice(0, 4);
}

export function mapCountryToOption(country) {
  return {
    label: country.name,
    value: country.countryID
  };
}

export const COUNTRY_CODE_PREFIX = '+';

export const getFormatedPhoneAndCountryCode = (phone, countryPhoneCodes) => {
  if (!phone || !countryPhoneCodes || !phone.startsWith('+')) return { phone };

  if (phone.startsWith('+')) {
    const matchingCode = countryPhoneCodes.map(c => '+' + c.PhoneCountryCode).find(c => phone.startsWith(c));
    return {
      phone: phone
        .replace(matchingCode, '')
        .replace(/ /g, '')
        .trim(),
      phoneCountryCode: matchingCode
    };
  } else if (phone.startsWith('00')) {
    const matchingCode = countryPhoneCodes.map(c => '00' + c.PhoneCountryCode).find(c => phone.startsWith(c));
    return {
      phone: phone
        .replace(matchingCode, '')
        .replace(/ /g, '')
        .trim(),
      phoneCountryCode: matchingCode.replace('00', COUNTRY_CODE_PREFIX)
    };
  }
  return { phone };
};

export function isInvalidBirthDate(day, month, year, invalidBirthdates) {
  if (day && month && year) {
    const birthDate = `${year}-${padZero(month)}-${padZero(day)}`;
    return invalidBirthdates.some(b => b === birthDate);
  }
  return false;
}

export function isPortugalCountry(country) {
  return country === 'Portugal';
}

export function hasDriverValues(driverInfo) {
  if (
    driverInfo.countryID &&
    driverInfo.driversLicense &&
    driverInfo.fullName &&
    driverInfo.birthDate &&
    driverInfo.address &&
    driverInfo.postalCode &&
    driverInfo.email &&
    driverInfo.documentID &&
    driverInfo.iDIssuedPlace
  )
  return true

  return false
}

export function useGetDriverInfo(documentNumber, documentType = DOCUMENT_TYPES.ID, enabled = false, handleSuccess) {
  return useQuery(
    ['get-driver-info', 'type', documentType, documentNumber],
    () => getDriverInfo(documentNumber, documentType),
    { 
      enabled,
      staleTime: 60 * 1000,
      onSuccess: (newDriverInfo) => {
        handleSuccess(newDriverInfo)
      }
   }
  )
}
